import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import moment from "moment";
import { Meeting, MeetingAttachment, MeetingAttendance } from "../../../interfaces/Meeting";
import { CustomField } from "../../../interfaces/Settings";
import { useSettingsStore } from "../../../stores/SettingsStore";
import { customNumberFormat, customNumberFormatString } from "../../../functions/general";
import { ExpandableElement } from "../../General/ExpandableElement";
import { useMeetingStore } from "../../../stores/MeetingsStore";
import { returnCustomFieldMeeting, returnCustomFieldMerchant, returnCustomFieldPipeline, returnFile, returnImage, returnLink, returnMerchantNameMeeting, returnMultiple, returnOption, returnParagraph, returnStatusMeeting } from "../../General/DetailMasterElement";
import { usePipelineStore } from "../../../stores/PipelineStore";

interface DetailMeetingProps {
    data: Meeting
    setShowDetailMeeting: React.Dispatch<React.SetStateAction<boolean>>
}

const ChatDetailMeeting: FC<DetailMeetingProps> = ({ data, setShowDetailMeeting }) => {
    const intl = useIntl()
    const [customMeeting, setCustomMeeting] = useState({} as any)
    const [customFields, setCustomFields] = useState<CustomField>({})
    const { settings } = useSettingsStore()
    const { selectMultiMeeting } = useMeetingStore()
    const { simplePipelines } = usePipelineStore()

    useEffect(() => {
        if (data) {
            const customData: any = {
                templateName: {
                    name: 'Database',
                    value: data.meetingTemplateName
                },
                attachments: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHOTO' }),
                    value: data.attachments?.length > 0 ? returnImage(data.attachments as MeetingAttachment[], intl) : "-"
                },
                merchant_name: {
                    name: settings.merchant_title,
                    value: returnMerchantNameMeeting(data, selectMultiMeeting, intl)
                },
                owner_name: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
                    value: data.owner_name
                },
                date_meet: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_STARTED' }),
                    value: data.allDay ? moment(data.date_meet).format("DD MMM YYYY") : moment(data.date_meet).format("DD MMM YYYY HH:mm")
                },
                date_meet_end: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.DATE_ENDED' }),
                    value: data.date_meet_end === null ? "-" : data.allDay ? moment(data.date_meet_end).format("DD MMM YYYY") : moment(data.date_meet_end).format("DD MMM YYYY HH:mm")
                },
                attendees: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PARTICIPANT' }),
                    value: data.attendees.length > 0 ? data.attendees.map((data: MeetingAttendance) => data.name).join(', ') : "-"
                },
                creator_name: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
                    value: data.creator_name
                },
                location: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LOCATION' }),
                    value: data.latitude && data.longitude ? data.location : "-"
                },
                reminderMinutes: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.REMINDER' }),
                    value: data.reminderMinutes === null ? "-" : customNumberFormat(parseInt(data.reminderMinutes)) + " Menit"
                },
                meeting_status: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.STATUS' }),
                    value: returnStatusMeeting(data, intl)
                },
                note: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NOTES' }),
                    value: data.note ? <ExpandableElement text={(data.note).replaceAll('\n', '<br/>')} maxHeight={100} /> : "-"
                },
            }

            const newData = {
                ...customData,
                ...Object.keys(customFields).reduce((acc, key) => {
                    let value;
                    const dataCustomFields = data.custom_fields[key]
                    const dataInfoCustomFields = (data as any)?.info_custom_fields?.[key];

                    if (customFields[key].type === 'date') {
                        value = dataCustomFields ? moment(dataCustomFields).format("DD MMM YYYY") : "-";
                    } else if (customFields[key].type === 'image' || customFields[key].type === 'images') {
                        value = dataCustomFields ? returnImage(dataCustomFields as string[], intl) : "-";
                    } else if (customFields[key].type === 'file' || customFields[key].type === 'files') {
                        value = dataCustomFields ? returnFile(dataCustomFields, intl) : "-";
                    } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                        value = dataCustomFields ? returnMultiple(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'link') {
                        value = dataCustomFields ? returnLink(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'dateTime') {
                        value = dataCustomFields ? moment(dataCustomFields).format('DD MMM YYYY HH:mm') : '-'
                    } else if (customFields[key].type === 'option') {
                        value = dataCustomFields ? returnOption(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'paragraph') {
                        value = dataCustomFields ? returnParagraph(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'serial') {
                        value = dataCustomFields ? (dataCustomFields as string[]).join('') : "-";
                    } else if (customFields[key].type === 'dateTimeRange') {
                        value = dataCustomFields ? (dataCustomFields as string[]).map((item: any) => moment(item).format("DD MMM YYYY HH:mm")).join(' - ') : '-';
                    } else if (customFields[key].type === 'merchant') {
                        value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                            <>
                                {index > 0 && ', '}
                                {returnCustomFieldMerchant(item)}
                            </>
                        )) : "-";
                    } else if (customFields[key].type === 'user') {
                        value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.name).join(', ') : "-";
                    } else if (customFields[key].type === 'pipeline') {
                        let result: any = simplePipelines.find((item: any) => item.id === (dataInfoCustomFields?.[0] as any)?.id)

                        if (result) {
                            let name = result.name + " / " + result.nominal + " / " + moment(result.dateCreated).format('DD MMM YYYY')
                            let idTemplate = (simplePipelines as any).find((item: any) => item.id === result.id)?.leadTemplateId
                            let idPipeline = result.id
                            value = returnCustomFieldPipeline(idTemplate!, idPipeline, name);
                        } else value = "-"
                    } else if (customFields[key].type === 'meeting') {
                        value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                            <>
                                {index > 0 && ', '}
                                {returnCustomFieldMeeting(item)}
                            </>
                        )) : "-";
                    } else if (customFields[key].type === 'time') {
                        let result = dataCustomFields ? moment(dataCustomFields).format('HH:mm') : '-'
                        value = result
                    } else if (customFields[key].type === 'number') {
                        value = dataCustomFields ? customNumberFormatString((dataCustomFields.toString())) : "-";
                    } else if (customFields[key].type === 'product') {
                        value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.product_name).join(', ') : "-";
                    }
                    else {
                        value = dataCustomFields ? dataCustomFields : "-";
                    }

                    return {
                        ...acc,
                        [key]: {
                            name: customFields[key].name,
                            value
                        }
                    };
                }, {})
            };

            setCustomMeeting(newData);
        }
    }, [customFields, data])

    useEffect(() => {
        if (Object.keys(selectMultiMeeting).length !== 0) {
            setCustomFields(selectMultiMeeting.customFields)
        }
    }, [selectMultiMeeting])

    return (
        <div className="card" style={{ minHeight: "calc(100vh - 1rem)" }}>
            <h3 className="py-3 px-5 d-flex align-items-center gap-3">
                <i className="fas fa-times fs-3 text-hover-primary cursor-pointer" onClick={() => setShowDetailMeeting(false)}></i>
                Detail {data.owner_name} {data.type === 'VISIT' && <span className="badge badge-light-primary ms-3" >{intl.formatMessage({ id: 'MEETING.STATUS.VISIT' })}</span>}
            </h3>
            <div className="card-body row" style={{ flex: "none" }}>
                {
                    Object.keys(customMeeting).map((key, index) => {
                        return (
                            <div className="row pb-2 col-12" key={index}>
                                <div className="col-4 text-end" style={{ fontWeight: "500", color: "#767373" }}>{customMeeting[key].name}</div>
                                <div className="col-8" style={{ fontWeight: "500", color: "black" }}>{customMeeting[key].value}</div>
                            </div>
                        )
                    })
                }
            </div>
        </div>
    )
}

export { ChatDetailMeeting }